import { DatePickerWithRange } from '@/components/date-picker-with-range'
import { Main } from '@/components/layout/main'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { DashboardDateRangeParams, DashboardService } from '@/services/dashboard.service'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { type DateRange } from 'react-day-picker'
import { DashboardCards } from './components/dashboard-cards'
import { DashboardCardsSkeleton } from './components/dashboard-cards-skeleton'
import { DashboardError } from './components/dashboard-error'
import { DashboardOverview } from './components/dashboard-overview'
import { DashboardOverviewSkeleton } from './components/dashboard-overview-skeleton'
import type {
  DashboardMetricsDTO,
  MonthlyConversationsDTO,
  RecentAttendancesDTO,
} from './types'

function buildDashboardParams(dateRange?: DateRange): DashboardDateRangeParams | undefined {
  if (!dateRange?.from || !dateRange?.to) return undefined

  return {
    startDate: format(dateRange.from, 'yyyy-MM-dd'),
    endDate: format(dateRange.to, 'yyyy-MM-dd'),
  }
}

export default function Dashboard() {
  const [metrics, setMetrics] = useState<DashboardMetricsDTO | null>(null)
  const [monthlyConversations, setMonthlyConversations] =
    useState<MonthlyConversationsDTO | null>(null)
  const [recentAttendances, setRecentAttendances] =
    useState<RecentAttendancesDTO | null>(null)
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async (range?: DateRange) => {
    try {
      setLoading(true)
      setError(null)

      const params = buildDashboardParams(range)
      const [metricsData, conversationsData, attendancesData] =
        await Promise.all([
          DashboardService.getMetrics(params),
          DashboardService.getMonthlyConversations(params),
          DashboardService.getRecentAttendances(params),
        ])

      setMetrics(metricsData)
      setMonthlyConversations(conversationsData)
      setRecentAttendances(attendancesData)
    } catch {
      setError('Não foi possível carregar os dados do dashboard')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchData(dateRange)
  }, [dateRange])

  return (
    <Main>
      <div className='mb-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between'>
        <div>
          <h1 className='text-2xl font-bold tracking-tight'>Dashboard</h1>
          <p className='text-sm text-muted-foreground'>O dashboard carrega o mês atual por padrão.</p>
        </div>
        <DatePickerWithRange
          value={dateRange}
          onChange={setDateRange}
          onClear={() => setDateRange(undefined)}
          label='Filtrar por período'
          placeholder='Mês atual'
          description='Selecione um intervalo opcional para filtrar todas as métricas.'
        />
      </div>
      <Tabs orientation='vertical' defaultValue='overview' className='space-y-4'>
        <div className='w-full overflow-x-auto pb-2'>
          <TabsList>
            <TabsTrigger value='overview'>Visão Geral</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value='overview' className='space-y-4'>
          {error ? (
            <DashboardError message={error} onRetry={() => fetchData(dateRange)} />
          ) : loading ? (
            <>
              <DashboardCardsSkeleton />
              <DashboardOverviewSkeleton />
            </>
          ) : (
            <>
              <DashboardCards metrics={metrics!} />
              <DashboardOverview
                monthlyConversations={monthlyConversations!}
                recentAttendances={recentAttendances!}
              />
            </>
          )}
        </TabsContent>
      </Tabs>
    </Main>
  )
}
